import type { ReactNode } from "react"

import { Card, CardContent, CardDescription, CardFooter, CardHeader } from "@/ui/card"
import { Large } from "@/ui/typography"
import { cn } from "@/ui/utils"

const featureVariants = {
  ai: {
    card: "hover:border-amber-500/30 hover:bg-amber-500/10",
    icon: "group-hover:bg-amber-500/20 group-hover:text-amber-600 dark:group-hover:text-amber-400",
    animation: "group-hover:animate-zap",
  },
  auth: {
    card: "hover:border-violet-500/30 hover:bg-violet-500/10",
    icon: "group-hover:bg-violet-500/20 group-hover:text-violet-600 dark:group-hover:text-violet-400",
    animation: "group-hover:animate-wiggle",
  },
  payments: {
    card: "hover:border-emerald-500/30 hover:bg-emerald-500/10",
    icon: "group-hover:bg-emerald-500/20 group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
    animation: "group-hover:animate-bounce",
  },
  permissions: {
    card: "hover:border-blue-500/30 hover:bg-blue-500/10",
    icon: "group-hover:bg-blue-500/20 group-hover:text-blue-600 dark:group-hover:text-blue-400",
    animation: "group-hover:animate-float",
  },
  database: {
    card: "hover:border-cyan-500/30 hover:bg-cyan-500/10",
    icon: "group-hover:bg-cyan-500/20 group-hover:text-cyan-600 dark:group-hover:text-cyan-400",
    animation: "group-hover:animate-jelly",
  },
  deploy: {
    card: "hover:border-sky-500/30 hover:bg-sky-500/10",
    icon: "group-hover:bg-sky-500/20 group-hover:text-sky-600 dark:group-hover:text-sky-400",
    animation: "group-hover:animate-spin",
  },
  ui: {
    card: "hover:border-pink-500/30 hover:bg-pink-500/10",
    icon: "group-hover:bg-pink-500/20 group-hover:text-pink-600 dark:group-hover:text-pink-400",
    animation: "group-hover:animate-tilt",
  },
  email: {
    card: "hover:border-orange-500/30 hover:bg-orange-500/10",
    icon: "group-hover:bg-orange-500/20 group-hover:text-orange-600 dark:group-hover:text-orange-400",
    animation: "group-hover:animate-float",
  },
  testing: {
    card: "hover:border-lime-500/30 hover:bg-lime-500/10",
    icon: "group-hover:bg-lime-500/20 group-hover:text-lime-600 dark:group-hover:text-lime-500",
    animation: "group-hover:animate-wobble",
  },
  blog: {
    card: "hover:border-indigo-500/30 hover:bg-indigo-500/10",
    icon: "group-hover:bg-indigo-500/20 group-hover:text-indigo-600 dark:group-hover:text-indigo-400",
    animation: "group-hover:animate-flip",
  },
  content: {
    card: "hover:border-teal-500/30 hover:bg-teal-500/10",
    icon: "group-hover:bg-teal-500/20 group-hover:text-teal-600 dark:group-hover:text-teal-400",
    animation: "group-hover:animate-slide",
  },
  newsletter: {
    card: "hover:border-rose-500/30 hover:bg-rose-500/10",
    icon: "group-hover:bg-rose-500/20 group-hover:text-rose-600 dark:group-hover:text-rose-400",
    animation: "group-hover:animate-wave",
  },
} as const

type FeatureVariant = keyof typeof featureVariants

interface FeaturesProps {
  children?: ReactNode
  className?: string
}

export const Features = ({ children, className }: FeaturesProps) => {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3 lg:gap-6 xl:gap-8",
        className,
      )}
    >
      {children}
    </div>
  )
}

interface FeatureItemProps {
  title: string
  icon: ReactNode
  variant?: FeatureVariant
  cta?: ReactNode
  children: ReactNode
}

const FeaturesItem = ({ title, icon, variant, cta, children }: FeatureItemProps) => {
  const hoverStyles = variant ? featureVariants[variant] : null

  return (
    <Card
      className={cn(
        "group h-full bg-card/50 transition-colors duration-300 ease-out",
        hoverStyles?.card,
      )}
    >
      <CardHeader>
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300 ease-out",
              hoverStyles?.icon,
              hoverStyles?.animation,
              "motion-reduce:group-hover:animate-none",
            )}
          >
            <span className="[&>svg]:size-5">{icon}</span>
          </div>
          <Large className="text-pretty">{title}</Large>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription>{children}</CardDescription>
      </CardContent>
      {cta ? <CardFooter className="mt-auto">{cta}</CardFooter> : null}
    </Card>
  )
}

FeaturesItem.displayName = "Features.Item"
Features.Item = FeaturesItem
